import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import { Typography, Card, CardContent, TextField, Grid, InputAdornment } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Page, Text, View, Document, PDFViewer } from "@react-pdf/renderer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController, useGlobalMDAlert } from "context";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CancelIcon from "@mui/icons-material/Cancel";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import {
  FP,
  PageHeader,
  StatCard,
  StatusPill,
  fpInputSx,
} from "components/financepro";
import { useApi } from "lib/api";

const parseNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).split(",").join(""));
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v) =>
  `$${parseNumber(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// El endpoint /api/informedescuentos devuelve { descuentos: [...], sucursales: [...] }.
// Normaliza cada fila a un shape uniforme.
function normalizeRow(raw) {
  const descinte = parseNumber(raw.descinte);
  const descmora = parseNumber(raw.descmora);
  return {
    norecibo: raw.norecibo ?? "",
    noprest: raw.noprest ?? "",
    nocuota: raw.nocuota ?? "",
    cedula: raw.cedula ?? "",
    cliente: raw.cliente ?? "",
    fecha: raw.fecha ?? "",
    fechaIso: raw.fecha_iso ?? "",
    sucursal: raw.sucursal ?? "",
    sucursaldescrip: raw.sucursaldescrip ?? "",
    usuario: raw.usuario ?? "",
    vpagint: parseNumber(raw.vpagint),
    vpagcap: parseNumber(raw.vpagcap),
    vpagmora: parseNumber(raw.vpagmora),
    descinte,
    descmora,
    totaldescuento: parseNumber(raw.totaldescuento) || descinte + descmora,
  };
}

function ReporteDescuentos() {
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  const [nosucursal, setNosucursal] = useState(null);
  const [sucursal, setSucursal] = useState([]);

  const [descuentos, setDescuentos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [presentarpdf, setPresentarpdf] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [reciboCollapsed, setReciboCollapsed] = useState({});
  const [cuotaExpanded, setCuotaExpanded] = useState({});

  const toggleGrupo = (key) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleRecibo = (key) =>
    setReciboCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleCuota = (key) =>
    setCuotaExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const [controller] = useMaterialUIController();
  const { nombreNegocio, roleWeb } = controller;
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    post("/api/recuperasucursales").then(({ status, data }) => {
      if (status === 200) {
        const list = data["sucursales"] || [];
        setSucursal([{ label: "0-TODOS" }, ...list]);
      }
      if (status === 400) {
        setSucursal([]);
        showAlert({ message: data["Error"], color: "error", autoHideDuration: 3000 });
      }
    });
  }, []);

  const rows = useMemo(
    () => (descuentos || []).map((d) => normalizeRow(d)),
    [descuentos]
  );

  const filteredRows = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.cliente, r.noprest, r.cedula, r.norecibo, r.usuario].some((v) =>
        String(v || "").toLowerCase().includes(q)
      )
    );
  }, [rows, busqueda]);

  // Agrupa por sucursal. Cada grupo lleva la lista de filas, totales y
  // contadores para la cabecera del card.
  const grupos = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((r) => {
      const sucKey = String(r.sucursal || "—");
      const sucLabel = r.sucursaldescrip
        ? `${sucKey} - ${r.sucursaldescrip}`
        : sucKey === "—"
        ? "Sin sucursal"
        : `Sucursal ${sucKey}`;
      if (!map.has(sucKey)) {
        map.set(sucKey, {
          key: sucKey,
          label: sucLabel,
          items: [],
          prestamosMap: new Map(),
          totalDescuento: 0,
          totalInteres: 0,
          totalMora: 0,
        });
      }
      const g = map.get(sucKey);
      g.items.push(r);
      g.totalDescuento += r.totaldescuento;
      g.totalInteres += r.descinte;
      g.totalMora += r.descmora;

      // Subgrupo por préstamo para totalizar el descuento de cada préstamo.
      const prestKey = String(r.noprest || "—");
      if (!g.prestamosMap.has(prestKey)) {
        g.prestamosMap.set(prestKey, {
          key: prestKey,
          noprest: r.noprest,
          cliente: r.cliente,
          cedula: r.cedula,
          items: [],
          recibosMap: new Map(),
          totalDescuento: 0,
          totalInteres: 0,
          totalMora: 0,
        });
      }
      const p = g.prestamosMap.get(prestKey);
      p.items.push(r);
      p.totalDescuento += r.totaldescuento;
      p.totalInteres += r.descinte;
      p.totalMora += r.descmora;

      // Subgrupo por recibo dentro del préstamo para totalizar por recibo.
      const reciboKey = String(r.norecibo || "—");
      if (!p.recibosMap.has(reciboKey)) {
        p.recibosMap.set(reciboKey, {
          key: reciboKey,
          norecibo: r.norecibo,
          noprest: r.noprest,
          cliente: r.cliente,
          cedula: r.cedula,
          fecha: r.fecha,
          usuario: r.usuario,
          items: [],
          totalDescuento: 0,
          totalInteres: 0,
          totalMora: 0,
        });
      }
      const rec = p.recibosMap.get(reciboKey);
      rec.items.push(r);
      rec.totalDescuento += r.totaldescuento;
      rec.totalInteres += r.descinte;
      rec.totalMora += r.descmora;
    });
    
    const list = Array.from(map.values());
    list.sort((a, z) => z.totalDescuento - a.totalDescuento);
    list.forEach((g) => {
      g.items.sort((a, z) => z.totaldescuento - a.totaldescuento);
      g.count = g.items.length;
      g.prestamos = Array.from(g.prestamosMap.values());
      g.prestamos.sort((a, z) => z.totalDescuento - a.totalDescuento);
      g.prestamos.forEach((p) => {
        p.items.sort((a, z) => z.totaldescuento - a.totaldescuento);
        p.count = p.items.length;
        p.recibos = Array.from(p.recibosMap.values());
        p.recibos.sort((a, z) => z.totalDescuento - a.totalDescuento);
        p.recibos.forEach((rec) => {
          rec.items.sort((a, z) => z.totaldescuento - a.totaldescuento);
          rec.count = rec.items.length;
        });
      });
    });
    console.log(list);
    return list;
    
  }, [filteredRows]);

  // Agrupa por préstamo para el PDF: suma descuentos del mismo noprest en una línea.
  const pdfRows = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((r) => {
      const key = String(r.noprest || "");
      if (!map.has(key)) {
        map.set(key, {
          noprest: r.noprest,
          cliente: r.cliente,
          fecha: r.fecha,
          fechaIso: r.fechaIso,
          totaldescuento: 0,
        });
      }
      const entry = map.get(key);
      entry.totaldescuento += r.totaldescuento;
      if ((r.fechaIso || "") >= (entry.fechaIso || "")) {
        entry.fecha = r.fecha;
        entry.fechaIso = r.fechaIso;
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const fa = a.fechaIso || "";
      const fb = b.fechaIso || "";
      if (fa !== fb) return fa.localeCompare(fb);
      return String(a.noprest).localeCompare(String(b.noprest));
    });
  }, [filteredRows]);

  const totalPdfRegistros = pdfRows.length;
  const totalPdfDescuento = pdfRows.reduce((acc, r) => acc + r.totaldescuento, 0);

  const expandAll = () => setCollapsed({});
  const collapseAll = () =>
    setCollapsed(Object.fromEntries(grupos.map((g) => [g.key, true])));

  function procesarrequerimiento() {
    if (!fechaDesde) {
      showAlert({ message: "Seleccione la fecha desde", color: "error", autoHideDuration: 3000 });
      return;
    }
    if (!fechaHasta) {
      showAlert({ message: "Seleccione la fecha hasta", color: "error", autoHideDuration: 3000 });
      return;
    }
    if (fechaDesde > fechaHasta) {
      showAlert({
        message: "La fecha desde no puede ser mayor que la fecha hasta",
        color: "error",
        autoHideDuration: 3000,
      });
      return;
    }

    const sucLabel = nosucursal?.label || "";
    const sucParam =
      !sucLabel || sucLabel.toUpperCase().includes("TODO")
        ? "Todos"
        : sucLabel;

    setCargando(true);
    post("/api/informedescuentos", {
      tipodeusuario: roleWeb || "",
      fechadesde: fechaDesde,
      fechahasta: fechaHasta,
      sucursal: sucParam,
    })
      .then(({ status, data }) => {
        if (status === 200) {
          const arr = data["descuentos"] || [];
          setDescuentos(arr);
          if (arr.length === 0) {
            showAlert({
              message: "No hay descuentos en el rango seleccionado",
              color: "info",
              autoHideDuration: 3000,
            });
          }
        } else {
          setDescuentos([]);
          showAlert({
            message: data["Error"] || "Error al generar reporte",
            color: "error",
            autoHideDuration: 3000,
          });
        }
      })
      .catch(() => {
        setDescuentos([]);
        showAlert({
          message: "Error de red al generar reporte",
          color: "error",
          autoHideDuration: 3000,
        });
      })
      .finally(() => setCargando(false));
  }

  const totalRegistros = filteredRows.length;
  const totalDescInteres = filteredRows.reduce((acc, r) => acc + r.descinte, 0);
  const totalDescMora = filteredRows.reduce((acc, r) => acc + r.descmora, 0);
  const totalDescuento = totalDescInteres + totalDescMora;
  const promedioDescuento = totalRegistros
    ? totalDescuento / totalRegistros
    : 0;

  function handlepdf() {
    if (filteredRows.length === 0) {
      showAlert({
        message: "No hay datos para procesar este reporte",
        color: "error",
        autoHideDuration: 3000,
      });
    } else {
      setPresentarpdf(true);
    }
  }

  const MyDocument = () => (
    <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%" }}>
      <Document>
        <Page size="A4" orientation="portrait">
          <View style={{ textAlign: "center", width: "94%", fontSize: 14, textTransform: "uppercase", marginTop: 15 }} fixed>
            <Text>{nombreNegocio}</Text>
          </View>
          <View style={{ textAlign: "center", width: "94%", fontSize: 9, textTransform: "uppercase" }} fixed>
            <Text>Reporte de Descuentos a Préstamos</Text>
          </View>
          <View style={{ textAlign: "center", width: "94%", fontSize: 9, marginBottom: 10 }} fixed>
            <Text>
              Periodo: {fechaDesde || "—"} a {fechaHasta || "—"}
            </Text>
          </View>

          <View style={{ marginLeft: 15, width: "94%" }}>
            <View style={{ flexDirection: "row", height: 20, fontSize: 9, backgroundColor: "#F4F6FB", color: "black", fontWeight: "bold" }}>
              <Text style={{ width: 80, padding: 3 }}>Préstamo #</Text>
              <Text style={{ flex: 1, padding: 3 }}>Cliente</Text>
              <Text style={{ width: 90, padding: 3 }}>Fecha</Text>
              <Text style={{ width: 100, padding: 3, textAlign: "right" }}>Total descuento</Text>
            </View>
            {pdfRows.map((r, i) => (
              <View key={i} style={{ flexDirection: "row", minHeight: 16, fontSize: 9, color: "black", borderBottomWidth: 0.5, borderBottomColor: "#E2E8F0" }}>
                <Text style={{ width: 80, padding: 3 }}>{r.noprest}</Text>
                <Text style={{ flex: 1, padding: 3 }}>{r.cliente}</Text>
                <Text style={{ width: 90, padding: 3 }}>{r.fecha}</Text>
                <Text style={{ width: 100, padding: 3, textAlign: "right" }}>{fmtMoney(r.totaldescuento)}</Text>
              </View>
            ))}
          </View>

          <View style={{ marginLeft: 15, marginTop: 10, width: "94%", flexDirection: "row", backgroundColor: "#E6EAF1", padding: 5 }}>
            <Text style={{ flex: 1, fontSize: 10, fontWeight: "bold" }}>TOTAL GENERAL</Text>
            <Text style={{ width: 100, fontSize: 9, textAlign: "right" }}>Préstamos: {totalPdfRegistros}</Text>
            <Text style={{ width: 130, fontSize: 9, textAlign: "right", fontWeight: "bold" }}>
              Total: {fmtMoney(totalPdfDescuento)}
            </Text>
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Préstamos"
        subtitle="Descuentos Aplicados"
        cta={
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            disabled={filteredRows.length === 0}
            onClick={handlepdf}
            sx={{
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
            }}
          >
            Generar PDF
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Filtros de Búsqueda
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  type="date"
                  label="Fecha desde"
                  value={fechaDesde}
                  onChange={(event) => setFechaDesde(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  type="date"
                  label="Fecha hasta"
                  value={fechaHasta}
                  onChange={(event) => setFechaHasta(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  disablePortal
                  options={sucursal || []}
                  value={nosucursal}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option?.label || "";
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const a = typeof option === "string" ? option : option?.label;
                    const b = typeof value === "string" ? value : value?.label;
                    return a === b;
                  }}
                  onChange={(_, value) => setNosucursal(value || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                disabled={cargando}
                startIcon={<SearchIcon />}
                onClick={procesarrequerimiento}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontSize: "0.94rem",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
                }}
              >
                {cargando ? "Procesando..." : "Procesar"}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <>
            <Box
              sx={{
                display: "grid",
                width: "100%",
                gap: 2,
                mb: 3,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
              }}
            >
              <StatCard
                label="Registros con descuento"
                value={String(totalRegistros)}
                icon={GroupsRoundedIcon}
                accent={FP.color.accentBlue}
              />
              <StatCard
                label="Total descuento"
                value={fmtMoney(totalDescuento)}
                icon={LocalOfferRoundedIcon}
                accent={FP.color.danger}
              />
              <StatCard
                label="Descuento de interés"
                value={fmtMoney(totalDescInteres)}
                icon={PercentRoundedIcon}
                accent={FP.color.warning}
              />
              <StatCard
                label="Descuento promedio"
                value={fmtMoney(promedioDescuento)}
                icon={PaidRoundedIcon}
                accent={FP.color.navy}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por cliente, préstamo, recibo, cédula o usuario"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...fpInputSx, flex: 1, minWidth: { xs: "100%", sm: 280 } }}
              />
              <Box sx={{ display: "flex", gap: 1, ml: { sm: "auto" } }}>
                <Button
                  size="small"
                  onClick={expandAll}
                  sx={{
                    color: FP.color.textSecondary,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
                  }}
                >
                  Expandir todo
                </Button>
                <Button
                  size="small"
                  onClick={collapseAll}
                  sx={{
                    color: FP.color.textSecondary,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
                  }}
                >
                  Colapsar todo
                </Button>
              </Box>
            </Box>
          </>
        )}

        {filteredRows.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              overflow: "hidden",
            }}
          >
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                  Seleccione el rango de fechas y la sucursal y haga clic en &quot;Procesar&quot; para obtener resultados.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          grupos.map((g) => {
            const isCollapsed = !!collapsed[g.key];
            return (
              <Card
                key={g.key}
                elevation={0}
                sx={{
                  mb: 2.5,
                  borderRadius: 2.5,
                  border: `1px solid ${FP.color.border}`,
                  bgcolor: FP.color.cardBg,
                  boxShadow: FP.shadow.soft,
                  overflow: "hidden",
                }}
              >
                <Box
                  onClick={() => toggleGrupo(g.key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleGrupo(g.key);
                    }
                  }}
                  aria-expanded={!isCollapsed}
                  sx={{
                    px: { xs: 2, md: 3 },
                    py: 1.4,
                    borderBottom: isCollapsed ? "none" : `1px solid ${FP.color.border}`,
                    bgcolor: "#FAFBFE",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    position: "relative",
                    cursor: "pointer",
                    userSelect: "none",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      bgcolor: FP.color.accentBlue,
                    },
                    "&:hover": { bgcolor: "#F4F6FB" },
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
                      {g.label}
                    </Typography>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.74rem", fontWeight: 600 }}>
                      {g.count} {g.count === 1 ? "registro" : "registros"} ·
                      {" "}Interés {fmtMoney(g.totalInteres)} ·
                      {" "}Mora {fmtMoney(g.totalMora)} ·
                      {" "}Total {fmtMoney(g.totalDescuento)}
                    </Typography>
                  </Box>
                  <StatusPill label={`${g.count}`} variant="info" />
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGrupo(g.key);
                    }}
                    aria-label={isCollapsed ? "Expandir" : "Colapsar"}
                    sx={{
                      color: FP.color.textSecondary,
                      transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    <ExpandMoreRoundedIcon />
                  </IconButton>
                </Box>
                {!isCollapsed && (
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                    {g.prestamos.map((p) => (
                      <Box key={p.key} sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            px: 1.4,
                            py: 1,
                            mb: 1.2,
                            borderRadius: 1.6,
                            bgcolor: FP.color.surfaceAlt,
                            border: `1px solid ${FP.color.border}`,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "0.92rem", lineHeight: 1.2 }}>
                              Préstamo #{p.noprest}
                            </Typography>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.74rem", fontWeight: 600, mt: 0.1, wordBreak: "break-word" }}>
                              {p.cliente || "—"} · {p.count} {p.count === 1 ? "registro" : "registros"} ·
                              {" "}Interés {fmtMoney(p.totalInteres)} ·
                              {" "}Mora {fmtMoney(p.totalMora)}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                              Total descuento del préstamo
                            </Typography>
                            <Typography sx={{ color: FP.color.danger, fontWeight: 800, fontSize: "1rem", fontVariantNumeric: "tabular-nums" }}>
                              {fmtMoney(p.totalDescuento)}
                            </Typography>
                          </Box>
                        </Box>
                        {p.recibos.map((rec) => {
                          const reciboKey = `${g.key}-${p.key}-${rec.key}`;
                          const isReciboCollapsed = !!reciboCollapsed[reciboKey];
                          return (
                          <Box key={rec.key} sx={{ mb: 1.4, "&:last-child": { mb: 0 } }}>
                            <Box
                              onClick={() => toggleRecibo(reciboKey)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  toggleRecibo(reciboKey);
                                }
                              }}
                              aria-expanded={!isReciboCollapsed}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                px: 1.2,
                                py: 0.7,
                                mb: isReciboCollapsed ? 0 : 0.9,
                                borderRadius: 1.4,
                                bgcolor: "#FFFFFF",
                                border: `1px dashed ${FP.color.border}`,
                                flexWrap: "wrap",
                                cursor: "pointer",
                                userSelect: "none",
                                "&:hover": { bgcolor: "#F4F6FB" },
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, minWidth: 0 }}>
                                <IconButton
                                  size="small"
                                  tabIndex={-1}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRecibo(reciboKey);
                                  }}
                                  aria-label={isReciboCollapsed ? "Expandir" : "Colapsar"}
                                  sx={{
                                    p: 0.2,
                                    color: FP.color.textSecondary,
                                    transform: isReciboCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                                    transition: "transform 0.2s ease",
                                  }}
                                >
                                  <ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.82rem", lineHeight: 1.2 }}>
                                    Recibo #{rec.norecibo}
                                  </Typography>
                                  <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.7rem", fontWeight: 600, mt: 0.1, wordBreak: "break-word" }}>
                                    Fecha {rec.fecha || "—"} · {rec.count} {rec.count === 1 ? "cuota" : "cuotas"}
                                    {rec.usuario ? ` · ${rec.usuario}` : ""}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                  Total descuento del recibo
                                </Typography>
                                <Typography sx={{ color: FP.color.danger, fontWeight: 800, fontSize: "0.9rem", fontVariantNumeric: "tabular-nums" }}>
                                  {fmtMoney(rec.totalDescuento)}
                                </Typography>
                              </Box>
                            </Box>
                            {!isReciboCollapsed && (
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0,1fr))" },
                                gap: 1.4,
                              }}
                            >
                              {rec.items.map((row, idx) => {
                                const cuotaKey = `${reciboKey}-${row.nocuota}-${idx}`;
                                const isCuotaExpanded = !!cuotaExpanded[cuotaKey];
                                return (
                                <Box
                                  key={`${row.norecibo}-${row.noprest}-${row.nocuota}-${idx}`}
                                  sx={{
                                    border: `1px solid ${FP.color.border}`,
                                    borderRadius: 2,
                                    p: 1.5,
                                    bgcolor: "#FFFFFF",
                                    boxShadow: FP.shadow.soft,
                                  }}
                                >
                                  <Box
                                    onClick={() => toggleCuota(cuotaKey)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleCuota(cuotaKey);
                                      }
                                    }}
                                    aria-expanded={isCuotaExpanded}
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      justifyContent: "space-between",
                                      gap: 1,
                                      mb: isCuotaExpanded ? 1 : 0,
                                      cursor: "pointer",
                                      userSelect: "none",
                                    }}
                                  >
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.6, minWidth: 0 }}>
                                      <IconButton
                                        size="small"
                                        tabIndex={-1}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleCuota(cuotaKey);
                                        }}
                                        aria-label={isCuotaExpanded ? "Colapsar" : "Expandir"}
                                        sx={{
                                          p: 0.2,
                                          mt: 0.2,
                                          color: FP.color.textSecondary,
                                          transform: isCuotaExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                                          transition: "transform 0.2s ease",
                                        }}
                                      >
                                        <ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />
                                      </IconButton>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1rem", lineHeight: 1.2 }}>
                                          Préstamo #{row.noprest} · Recibo #{row.norecibo}
                                        </Typography>
                                        <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.85rem", fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}>
                                          {row.cliente || "—"}
                                        </Typography>
                                        <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem", mt: 0.1 }}>
                                          {row.cedula || "—"} · Fecha {row.fecha || "—"}
                                          {row.usuario ? ` · ${row.usuario}` : ""}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.6 }}>
                                      <StatusPill label={fmtMoney(row.totaldescuento)} variant="danger" />
                                      {row.nocuota ? (
                                        <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.7rem", fontWeight: 700 }}>
                                          Cuota #{row.nocuota}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                  </Box>
                                  {isCuotaExpanded && (
                                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 1 }}>
                                    {[
                                      ["Descuento interés", fmtMoney(row.descinte)],
                                      ["Descuento mora", fmtMoney(row.descmora)],
                                      ["Pago interés", fmtMoney(row.vpagint)],
                                      ["Pago capital", fmtMoney(row.vpagcap)],
                                    ].map(([label, value]) => (
                                      <Box key={label}>
                                        <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                          {label}
                                        </Typography>
                                        <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.92rem", fontWeight: 600, fontVariantNumeric: "tabular-nums", wordBreak: "break-word" }}>
                                          {value ?? "—"}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                  )}
                                </Box>
                                );
                              })}
                            </Box>
                            )}
                          </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })
        )}

        {filteredRows.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" }, mt: 1 }}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlepdf}
              sx={{
                width: { xs: "100%", sm: "auto" },
                borderColor: FP.color.danger,
                color: FP.color.danger,
                borderRadius: 1.2,
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
              }}
            >
              Generar PDF
            </Button>
          </Box>
        )}

        {presentarpdf === true && (
          <Dialog
            open={presentarpdf}
            maxWidth="lg"
            fullWidth
            fullScreen={isXs}
            onClose={() => setPresentarpdf(false)}
            PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
          >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight="bold">
                Vista Previa de Descuentos a Préstamos
              </Typography>
              <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: "auto", p: 1 }}>
                <CancelIcon />
              </Button>
            </DialogTitle>
            <DialogContent>
              <MyDocument />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </Box>
  );
}

export default ReporteDescuentos;
